import type { ComponentPropsWithoutRef } from 'react'
import { useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { load } from '@fingerprintjs/botd'
import { useFormContext } from 'react-hook-form'
import { graphql, useMutation } from 'relay-hooks'

import { Banner } from 'design-system/components/Banner'
import Button from 'design-system/components/Button'
import { InputStyling } from 'design-system/components/Input'
import LoadingSpinner from 'design-system/components/LoadingSpinner'
import colors from 'design-system/tokens/colors'
import fontSizes from 'design-system/tokens/fontSizes'
import fontWeights from 'design-system/tokens/fontWeights'
import px from 'design-system/tokens/iconSizes'
import spacing from 'design-system/tokens/spacing'

// eslint-disable-next-line no-restricted-imports -- use datadog and rollbar directly
import logging from 'shared/utils/logging'
import PhoneField from 'web-client/components/Form/fields/PhoneField'
import type { CreateDirectMobileWebJobMutation } from './__generated__/CreateDirectMobileWebJobMutation.graphql'

const CREATE_DIRECT_MOBILE_WEB_JOB = graphql`
  mutation CreateDirectMobileWebJobMutation($input: CreateDirectMobileWebJobInput!) {
    createDirectMobileWebJob(input: $input) {
      ... on Job {
        id
        mobileIntakeUrl
      }
      ... on MutationErrors {
        errors {
          messages
        }
      }
    }
  }
`

const MainContent = styled.div`
  flex: 1;
  padding: ${spacing.xl} ${spacing.s};
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  gap: ${spacing.xs};
  max-width: 450px;
`

const Title = styled.h1`
  font-size: ${fontSizes.h1};
  font-weight: ${fontWeights.bold};
  color: ${colors.black};
  margin: 0;
  line-height: 1.2;
`

const Subtitle = styled.p`
  font-size: ${fontSizes.bodyLarge};
  color: ${colors.grays.l1};
  margin: 0 0 ${spacing.s};
  line-height: 1.4;
`

const InputLabel = styled.label`
  text-align: left;
  font-size: ${fontSizes.body};
  color: ${colors.grays.l1};
  margin-bottom: ${spacing.xxxs};
  line-height: ${fontSizes.bodyLarge};
`

const StyledPhoneField = styled(PhoneField)`
  ${(props) => InputStyling(props)}
  font-size: ${fontSizes.bodyLarge};
  height: 48px;
  margin-top: ${spacing.xxxs};
  width: 100%;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spacing.xs};
`

const HoneypotWrapper = styled.div`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`

const LoadingSpinnerContainer = styled.div`
  height: ${px.large};
  width: ${px.large};
  margin: ${spacing.xs};
`

const GetStartedButton = styled(Button)`
  display: flex;
  justify-content: center;
  text-transform: uppercase;
`

const DisclaimerText = styled.p`
  font-size: ${fontSizes.bodySmall};
  color: ${colors.grays.l2};
  line-height: 1.4;
  margin: 0;
`

const FloatingBanner = styled(Banner)`
  align-self: center;
  margin: ${spacing.m1};
  position: absolute;
  top: 5%;
  width: -webkit-fill-available;
`

const ValidationError = styled.div`
  color: ${colors.alerts.red};
  font-size: ${fontSizes.body};
  line-height: 20px;
  padding-bottom: ${spacing.xxxs};
`

const bannerProps: Record<string, ComponentPropsWithoutRef<typeof FloatingBanner>> = {
  success: {
    type: 'success',
    message: 'Text message request successfully sent! Tap the link in the text to request service',
  },
  error: { type: 'error', message: 'A system error occurred, please try again' },
}

type Props = {
  onSuccess: () => void
}

const Create = ({ onSuccess }: Props) => {
  const [bot, setBot] = useState(false)
  const [createDirectMWIJob, { loading }] = useMutation<CreateDirectMobileWebJobMutation>(
    CREATE_DIRECT_MOBILE_WEB_JOB
  )
  const [bannerType, setBannerType] = useState<'success' | 'error' | null>(null)
  useEffect(() => {
    try {
      window.sessionStorage.setItem('directMWI-phoneNumber', '')
    } catch (error) {
      logging.logError('Error clearing session storage for directMWI-phoneNumber', { error })
    }
    const botdPromise = load()
    botdPromise
      .then((botDetector) => {
        return botDetector.detect()
      })
      .then((result) => result?.bot ?? true)
      .catch((error) => {
        setBot(true)
        logging.logError('Error loading Botd', { error })
      })
  }, [])

  const clientCode = window.gon.client_code ?? ''

  const {
    register,
    handleSubmit,
    watch,
    formState: { isDirty, isValid },
  } = useFormContext()
  const phoneNumber = watch('phoneNumber')
  const honeyPot = watch('name')

  const shouldRedirectToMWI = window.gon?.should_redirect_to_mwi === true
  const onSubmit = async () => {
    if (bot || honeyPot) {
      setTimeout(() => {
        setBannerType('error')
      }, 1000)
      return
    }
    await createDirectMWIJob({
      variables: {
        input: {
          clientCode,
          phone: phoneNumber,
        },
      },
      cacheConfig: {
        metadata: {
          headers: {
            'direct-mobile-web-token': window.gon.direct_mobile_web_token ?? '',
          },
        },
      },
      onCompleted: (response) => {
        const { id, mobileIntakeUrl } = response.createDirectMobileWebJob || {}
        if (id) {
          try {
            window.sessionStorage.setItem('directMWI-phoneNumber', phoneNumber as string)
          } catch (error) {
            logging.logError('Error saving phone number to session storage', { error })
          }
          if (shouldRedirectToMWI && mobileIntakeUrl && mobileIntakeUrl.includes('/txt')) {
            const mobileIntakePath = mobileIntakeUrl.substring(mobileIntakeUrl.indexOf('/txt'))
            window.location.href = mobileIntakePath
          } else {
            onSuccess()
          }
        } else {
          setBannerType('error')
        }
      },
      onError: () => {
        setBannerType('error')
      },
    })
  }
  return (
    <MainContent>
      {bannerType && <FloatingBanner {...bannerProps[bannerType]} />}
      <Title>Need Roadside Assistance?</Title>
      <Subtitle>
        Start your request here. We&apos;ll send you a text to easily connect with a provider near
        you.
      </Subtitle>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <HoneypotWrapper>
          <input autoComplete="off" tabIndex={-1} type="text" {...register('name')} />
        </HoneypotWrapper>
        <InputLabel htmlFor="phoneNumber">
          Enter your phone number
          <StyledPhoneField autoComplete="tel" hideLabel name="phoneNumber" required />
          {!isValid && isDirty && (
            <ValidationError>
              Please enter a valid number in this format: ###-###-####
            </ValidationError>
          )}
        </InputLabel>

        <GetStartedButton disabled={!isValid || loading} size="l" type="submit">
          {loading ? (
            <LoadingSpinnerContainer>
              <LoadingSpinner circleColor={colors.grays.l6} progressColor={colors.grays.l4} />
            </LoadingSpinnerContainer>
          ) : (
            'Get Started'
          )}
        </GetStartedButton>
      </StyledForm>

      <DisclaimerText>
        By proceeding, you consent to receive text messages at the phone number provided. Message
        and data rates may apply.
      </DisclaimerText>
    </MainContent>
  )
}

export default Create
